import { t } from 'i18next';
import Cookies from 'js-cookie';
import { LatLngTuple } from 'leaflet';
import { toast } from 'react-toastify';
import { ZodSchema } from 'zod';

import {
  ALERT_LIFECYCLE_RESOLVED,
  ALERT_LIFECYCLE_UNRESOLVED,
  MISSION_LIFECYCLE_ABORTED,
  MISSION_LIFECYCLE_ARRIVAL,
  MISSION_LIFECYCLE_ARRIVED,
  MISSION_LIFECYCLE_DEPARTURE,
  MISSION_LIFECYCLE_ON_THE_WAY,
  MISSION_LIFECYCLE_OUT_OF_ITINERARY,
  MISSION_LIFECYCLE_PENDING,
  MISSION_LIFECYCLE_REJECTED,
  MISSION_LIFECYCLE_START,
  MISSION_LIFECYCLE_SUCCESS,
  SHIFT_STATUS_CANCEL,
  SHIFT_STATUS_FINISHED,
  SHIFT_STATUS_ONGOING,
  SHIFT_STATUS_PAUSED,
  SHIFT_STATUS_VALIDATE,
} from '../constants.ts';
import { IMission } from '../schema/Mission.ts';
import { NodeType } from '../schema/Node.ts';

export function GetMapCenterCoordinated(nodes: NodeType[]): LatLngTuple {
  if (nodes.length === 0) return [0, 0];
  const average = (arr: number[]) => arr.reduce((sum, val) => sum + val, 0) / arr.length;
  return [average(nodes.map(n => n.position.lat)), average(nodes.map(n => n.position.lon))];
}

export function GetMapCenterWithGeolocations(geolocations: number[][]): LatLngTuple {
  if (geolocations.length === 0) return [0, 0];
  const average = (arr: number[]) => arr.reduce((sum, val) => sum + val, 0) / arr.length;
  return [average(geolocations.map(g => g[0])), average(geolocations.map(g => g[1]))];
}

export function FormatDate(rawDate: string): string {
  const rawDateSec = new Date(rawDate);
  return rawDateSec.toLocaleString();
}

export function FormatTime(rawDate: string, second: boolean): string {
  const rawDateSec = new Date(rawDate);
  const format: Intl.DateTimeFormatOptions = {
    hour: '2-digit',
    minute: '2-digit',
  };
  if (second) {
    format['second'] = '2-digit';
  }
  return new Date(rawDateSec).toLocaleTimeString([], format);
}

export function getNodeNameById(nodes: NodeType[], node_id: number | null) {
  if (!nodes || !node_id) return;
  return nodes.find(node => node.id === node_id.toString())?.name;
}

interface Colors {
  color: string;
  backgroundColor: string;
}
export function getMissionLifecycleColors(lifecycle: string): Colors {
  let colors: Colors;
  switch (lifecycle) {
    case MISSION_LIFECYCLE_PENDING:
      colors = { color: '#717587', backgroundColor: '#E8E8EC' };
      break;
    case MISSION_LIFECYCLE_START:
      colors = { color: '#0078FF', backgroundColor: '#C5E7FE' };
      break;
    case MISSION_LIFECYCLE_DEPARTURE:
      colors = { color: '#A45CFF', backgroundColor: '#E3CDFF' };
      break;
    case MISSION_LIFECYCLE_ARRIVAL:
      colors = { color: '#0078FF', backgroundColor: '#C5E7FE' };
      break;
    case MISSION_LIFECYCLE_ARRIVED:
      colors = { color: '#0078FF', backgroundColor: '#C5E7FE' };
      break;
    case MISSION_LIFECYCLE_ON_THE_WAY:
      colors = { color: '#A45CFF', backgroundColor: '#E3CDFF' };
      break;
    case MISSION_LIFECYCLE_SUCCESS:
      colors = { color: '#198155', backgroundColor: '#ECFCE5' };
      break;
    case MISSION_LIFECYCLE_REJECTED:
      colors = { color: '#D3180C', backgroundColor: '#FFE5E5' };
      break;
    case MISSION_LIFECYCLE_ABORTED:
      colors = { color: '#D3180C', backgroundColor: '#FFE5E5' };
      break;
    case MISSION_LIFECYCLE_OUT_OF_ITINERARY:
      colors = { color: '#D3180C', backgroundColor: '#FFE5E5' };
      break;
    default:
      colors = { color: '#D3180C', backgroundColor: '#FFE5E5' };
  }
  return colors;
}

export function getAlertLifecycleColors(lifecycle: string): Colors {
  let colors: Colors;
  switch (lifecycle) {
    case ALERT_LIFECYCLE_UNRESOLVED:
      colors = { color: '#D3180C', backgroundColor: '#FFE5E5' };
      break;
    case ALERT_LIFECYCLE_RESOLVED:
      colors = { color: '#198155', backgroundColor: '#ECFCE5' };
      break;
    default:
      colors = { color: '#D3180C', backgroundColor: '#FFE5E5' };
  }
  return colors;
}

export function getShiftStatusColors(status: string): Colors {
  let colors: Colors;
  switch (status) {
    case SHIFT_STATUS_VALIDATE:
      colors = { color: '#0078FF', backgroundColor: '#C5E7FE' };
      break;
    case SHIFT_STATUS_ONGOING:
      colors = { color: '#198155', backgroundColor: '#ECFCE5' };
      break;
    case SHIFT_STATUS_CANCEL:
      colors = { color: '#D3180C', backgroundColor: '#FFE5E5' };
      break;
    case SHIFT_STATUS_PAUSED:
      colors = { color: '#717587', backgroundColor: '#E8E8EC' };
      break;
    case SHIFT_STATUS_FINISHED:
      colors = { color: '#A45CFF', backgroundColor: '#E3CDFF' };
      break;
    default:
      colors = { color: '#D3180C', backgroundColor: '#FFE5E5' };
  }
  return colors;
}

export function getGeographyDistanceColors(distance: number): Colors {
  let colors: Colors;
  if (distance < 1) {
    colors = { color: '#198155', backgroundColor: '#ECFCE5' };
  } else if (1 <= distance && distance <= 5) {
    colors = { color: 'white', backgroundColor: '#FFDE77' };
  } else {
    colors = { color: '#D3180C', backgroundColor: '#FFE5E5' };
  }
  return colors;
}

interface LogLevel {
  name: string;
  color: string;
  backgroundColor: string;
}

export function getLogLevel(logLevel: number | string): LogLevel {
  switch (logLevel) {
    case 10:
    case 'debug':
      return { name: 'Debug', color: '#0078FF', backgroundColor: '#C5E7FE' };
    case 20:
    case 'info':
      return { name: 'Info', color: '#A45CFF', backgroundColor: '#E3CDFF' };
    case 30:
    case 'warning':
      return { name: 'Warning', color: '#FCBC06', backgroundColor: '#FFDE77' };
    case 40:
    case 'error':
      return { name: 'Error', color: '#D3180C', backgroundColor: '#FFE5E5' };
    case 50:
    case 'critical':
      return { name: 'Critical', color: '#D3180C', backgroundColor: '#FFE5E5' };
    default:
      return { name: 'Notset', color: '#0078FF', backgroundColor: '#C5E7FE' };
  }
}

export function getFlagName(language: string): string {
  let flagName: string;
  flagName = 'en';
  if (language.startsWith('en')) {
    flagName = 'en';
  } else if (language.startsWith('fr')) {
    flagName = 'fr';
  } else if (language.startsWith('it')) {
    flagName = 'it';
  }
  return flagName;
}

export function getToken(): string | undefined {
  return Cookies.get('token');
}
export function setToken(token: string): void {
  const secure: boolean = import.meta.env.VITE_ENV !== 'LOCAL';
  Cookies.set('token', token, { expires: 1, secure: secure, sameSite: 'strict' });
}

export function removeToken(): void {
  Cookies.remove('token');
}

export function getIsoFormatDatetime(hours: number, yesterday: boolean): string {
  const now = new Date();
  now.setHours(hours, 0, 0, 0);
  if (yesterday) {
    now.setDate(now.getDate() - 1);
  }
  return now.toISOString();
}

export function exportIntoCSV(header: string[], data: string[][], outputName: string) {
  const csvRows = [header, ...data];
  const csvString = csvRows.map(row => row.join(';')).join('\n');
  const blob = new Blob([csvString], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = outputName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
export function convertAntDesignDateToStr(date) {
  return date.format('YYYY-MM-DDTHH:mm:ss');
}

export function validateData<T>(schema: ZodSchema<T>, data: unknown, componentIdentifier: string): T {
  const result = schema.safeParse(data);
  if (!result.success) {
    console.error('Zod Validation Error:', componentIdentifier, data, result.error);
    toast.error(`${componentIdentifier} component data validation error`);
    throw result.error;
  }
  return result.data;
}

export function haversineDistance(lat1: number, lon1: number, lat2: number, lon2: number) {
  const R = 6371000; // rayon Terre en mètres
  const toRad = (x: number) => (x * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) * Math.sin(dLon / 2);
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

export function getAlertWording(code: number): string {
  return t(`alert.${code}`);
}

export function computeMissionProgress(mission: IMission): string {
  return mission.lifecycle === MISSION_LIFECYCLE_SUCCESS ? '100' : mission.progress;
}
